/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { reportError } from '@/shared/ui/components/basic/error-notification/report-error';
import { FF_APP_POST_IMAGE_EDIT } from '@/shared/utils/feature-flag';
import {
  isInstagramEditHidden,
  isInstagramEditNotSupportedError,
} from '@/shared/utils/instagram-edit-policy';
import type { PublicationRow } from './types';
import { refusalMessage } from './portal-refusal';
import {
  buildSaveBody,
  deletePublication,
  fetchPostContent,
  rowIdBody,
  saveRow,
} from './use-publications-actions';

type Translate = (key: string) => string;

type EditState = {
  setEditingRow: (v: string | null) => void;
  setEditText: (v: string) => void;
  setEditImages: (v: string[]) => void;
  setEditImagesOriginal: (v: string[]) => void;
  setLoadingEditContent: (v: boolean) => void;
};

/**
 * Applies loaded post content to the edit fields, falling back to the property
 * images when the post carries no image list.
 *
 * @param content - The loaded post content (message and optional images).
 * @param propertyImages - Fallback image URLs from the property.
 * @param edit - Edit-state setters to populate.
 */
function applyEditContent(content: Record<string, unknown>, propertyImages: string[], edit: EditState) {
  if (typeof content.message === 'string') edit.setEditText(content.message);
  const apiImages = Array.isArray(content.images) ? (content.images as string[]) : null;
  const imagesToUse = apiImages !== null ? apiImages : propertyImages;
  edit.setEditImages(imagesToUse);
  edit.setEditImagesOriginal(imagesToUse);
}

/**
 * Determines whether a save should be blocked because a portal that requires
 * images (all but Facebook, when the feature flag is on) has none.
 *
 * @param row - The publication row being edited.
 * @param editImages - The current edit image list.
 * @returns True when required images are missing.
 */
function isImageRequiredButMissing(row: PublicationRow, editImages: string[]): boolean {
  return FF_APP_POST_IMAGE_EDIT && editImages.length === 0 && row.portal !== 'facebook' && row.portal !== 'olx';
}

type EditingContext = {
  agencyId: string;
  propertyId: string;
  propertyImages: string[];
  t: Translate;
  refresh: () => Promise<void>;
  onSaveSuccess?: () => void;
};

/**
 * Deletes a publication whose remote post is gone and notifies the user
 * (best-effort; errors are swallowed).
 *
 * @param ctx - Editing context with agency/property ids, translator, refresh.
 * @param row - The publication row to clean up.
 */
async function cleanupMissingPost(ctx: EditingContext, row: PublicationRow): Promise<void> {
  try {
    await deletePublication(ctx.agencyId, ctx.propertyId, row.portal, rowIdBody(row));
    await ctx.refresh();
    toast.success(ctx.t('publications.toasts.postNotFound'));
  } catch {
    // silent — best-effort cleanup
  }
}

/**
 * Loads an editable post's content into the edit fields, cleaning up the
 * publication when the remote post no longer exists.
 *
 * @param ctx - Editing context with agency/property ids and helpers.
 * @param row - The publication row being edited.
 * @param postId - Identifier of the remote post to load.
 * @param edit - Edit-state setters to populate.
 */
async function loadEditablePostContent(
  ctx: EditingContext,
  row: PublicationRow,
  postId: string,
  edit: EditState,
): Promise<void> {
  const contentPortal = row.portal === 'instagram' ? 'instagram' : 'facebook';
  edit.setLoadingEditContent(true);
  try {
    const content = await fetchPostContent(ctx.agencyId, contentPortal, postId);
    if (!content) {
      edit.setEditingRow(null);
      await cleanupMissingPost(ctx, row);
      return;
    }
    applyEditContent(content, ctx.propertyImages, edit);
  } catch {
    // silent — user can type manually
  } finally {
    edit.setLoadingEditContent(false);
  }
}

type SaveEditParams = {
  ctx: EditingContext;
  row: PublicationRow;
  editText: string;
  editImages: string[];
  editImagesOriginal: string[];
  setUpdating: (v: string | null) => void;
  resetEditFields: (v: string | null) => void;
};

/**
 * Persists the edited row (text + images), refreshes the list, and surfaces
 * success or error toasts.
 *
 * @param params - Editing context, row, edited fields, and state setters.
 */
async function persistRowEdit(params: SaveEditParams): Promise<void> {
  const { ctx, row, editText, editImages, editImagesOriginal, setUpdating, resetEditFields } = params;
  if (isInstagramEditHidden(row.portal)) {
    toast.error(ctx.t('publications.toasts.instagramEditUnsupported'));
    return;
  }
  setUpdating(row.key);
  try {
    const body = buildSaveBody(row, editText, editImages, editImagesOriginal);
    await saveRow(ctx.agencyId, ctx.propertyId, row.portal, body);
    resetEditFields(null);
    await ctx.refresh();
    toast.success(ctx.t('publications.toasts.updateSuccess'));
    ctx.onSaveSuccess?.();
  } catch (error) {
    reportError(error, { message: resolveUpdateErrorMessage(error, ctx.t) });
  } finally {
    setUpdating(null);
  }
}

/**
 * Maps API edit errors to a user-facing toast message.
 */
function resolveUpdateErrorMessage(error: unknown, t: Translate): string {
  if (isInstagramEditNotSupportedError(error)) {
    return t('publications.toasts.instagramEditUnsupported');
  }
  return refusalMessage(error, 'publications.toasts.updateError', t);
}

/**
 * Manages the edit-panel state and the load/save handlers for a single publication row,
 * keeping the parent publications hook thin.
 */
export function usePublicationEditing(ctx: EditingContext) {
  const { propertyImages } = ctx;
  const [updating, setUpdating] = useState<string | null>(null);
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [editText, setEditText] = useState<string>('');
  const [loadingEditContent, setLoadingEditContent] = useState(false);
  const [editImages, setEditImages] = useState<string[]>([]);
  const [editImagesOriginal, setEditImagesOriginal] = useState<string[]>([]);
  const editState: EditState = { setEditingRow, setEditText, setEditImages, setEditImagesOriginal, setLoadingEditContent };

  const resetEditFields = useCallback((row: string | null) => {
    setEditingRow(row);
    setEditImages([]);
    setEditImagesOriginal([]);
    setEditText('');
  }, []);

  const handleEdit = useCallback(async (row: PublicationRow) => {
    if (editingRow === row.key) return;
    resetEditFields(row.key);
    // OLX has no post text to load: the ad is re-sent from the property.
    if (row.portal === 'olx') return;
    if (!row.post_id) {
      setEditImages(propertyImages);
      setEditImagesOriginal(propertyImages);
      return;
    }
    await loadEditablePostContent(ctx, row, row.post_id, editState);
  }, [editingRow, propertyImages, resetEditFields, ctx, editState]);

  const handleEditSave = useCallback(async (row: PublicationRow) => {
    if (isImageRequiredButMissing(row, editImages)) {
      toast.error(ctx.t('publications.errors.imagesRequired'));
      return;
    }
    await persistRowEdit({ ctx, row, editText, editImages, editImagesOriginal, setUpdating, resetEditFields });
  }, [ctx, editText, editImages, editImagesOriginal, resetEditFields]);

  return {
    updating, editingRow, editText, setEditText, loadingEditContent, editImages, editImagesOriginal, setEditImages,
    handleEdit, handleEditSave, cancelEdit: () => resetEditFields(null),
  };
}
